import { useState } from 'react';
import { X } from 'lucide-react';
import { useWizardStore } from '../../stores/wizardStore';
import { useWizardOptions } from '../../hooks/useWizardOptions';
import clsx from 'clsx';

export default function GitWorkflowStep() {
  const { gitWorkflow, updateGitWorkflow } = useWizardStore();
  const { options, loading } = useWizardOptions();
  const [branchInput, setBranchInput] = useState('');

  const branchingStrategies = options?.gitWorkflow.branchingStrategies ?? [];
  const branchNamingConventions = options?.gitWorkflow.branchNamingConventions ?? [];
  const commitStandards = options?.gitWorkflow.commitStandards ?? [];
  const prRequirementOptions = options?.gitWorkflow.prRequirementOptions ?? [];

  if (loading) return <div className="text-gray-500 text-sm py-8 text-center">Loading configuration...</div>;

  const togglePRRequirement = (value: string) => {
    const current = gitWorkflow.prRequirements;
    if (current.includes(value)) {
      updateGitWorkflow({ prRequirements: current.filter((r) => r !== value) });
    } else {
      updateGitWorkflow({ prRequirements: [...current, value] });
    }
  };

  const addProtectedBranch = () => {
    const trimmed = branchInput.trim();
    if (trimmed && !gitWorkflow.protectedBranches.includes(trimmed)) {
      updateGitWorkflow({
        protectedBranches: [...gitWorkflow.protectedBranches, trimmed],
      });
      setBranchInput('');
    }
  };

  const removeProtectedBranch = (branch: string) => {
    updateGitWorkflow({
      protectedBranches: gitWorkflow.protectedBranches.filter((b) => b !== branch),
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">
          Git Workflow
        </h2>
        <p className="text-sm text-gray-500">
          Define how your team uses Git so the AI follows the same conventions.
        </p>
      </div>

      {/* Branching Strategy */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Branching Strategy
        </label>
        <div className="grid gap-2">
          {branchingStrategies.map((strategy) => (
            <label
              key={strategy.value}
              className={clsx(
                'flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all duration-200',
                gitWorkflow.branchingStrategy === strategy.value
                  ? 'bg-accent/10 border-accent/30'
                  : 'bg-surface-DEFAULT border-dark-border hover:border-surface-300'
              )}
            >
              <input
                type="radio"
                name="branchingStrategy"
                value={strategy.value}
                checked={gitWorkflow.branchingStrategy === strategy.value}
                onChange={(e) => updateGitWorkflow({ branchingStrategy: e.target.value })}
                className="sr-only"
              />
              <div
                className={clsx(
                  'w-4 h-4 rounded-full border-2 mt-0.5 shrink-0 transition-colors',
                  gitWorkflow.branchingStrategy === strategy.value
                    ? 'border-accent bg-accent'
                    : 'border-surface-300'
                )}
              >
                {gitWorkflow.branchingStrategy === strategy.value && (
                  <div className="w-full h-full rounded-full flex items-center justify-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-white" />
                  </div>
                )}
              </div>
              <div>
                <div className="text-sm font-medium text-white">
                  {strategy.label}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {strategy.desc}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Branch Naming */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Branch Naming Convention
        </label>
        <select
          value={gitWorkflow.branchNaming}
          onChange={(e) => updateGitWorkflow({ branchNaming: e.target.value })}
          className="select-field"
        >
          <option value="">Select naming convention...</option>
          {branchNamingConventions.map((conv) => (
            <option key={conv.value} value={conv.value}>
              {conv.label}
            </option>
          ))}
        </select>
        {gitWorkflow.branchNaming && (
          <div className="mt-2 p-3 rounded-lg bg-surface-DEFAULT border border-dark-border">
            <span className="text-xs text-gray-500">Preview: </span>
            <code className="text-xs text-accent font-mono">
              {branchNamingConventions.find((c) => c.value === gitWorkflow.branchNaming)?.example}
            </code>
          </div>
        )}
      </div>

      {/* Commit Standard */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Commit Message Standard
        </label>
        <select
          value={gitWorkflow.commitStandard}
          onChange={(e) => updateGitWorkflow({ commitStandard: e.target.value })}
          className="select-field"
        >
          <option value="">Select commit standard...</option>
          {commitStandards.map((std) => (
            <option key={std.value} value={std.value}>
              {std.label}
            </option>
          ))}
        </select>
        {gitWorkflow.commitStandard && (
          <div className="mt-2 p-3 rounded-lg bg-surface-DEFAULT border border-dark-border">
            <span className="text-xs text-gray-500">Example: </span>
            <code className="text-xs text-green-400 font-mono">
              {commitStandards.find((c) => c.value === gitWorkflow.commitStandard)?.example}
            </code>
          </div>
        )}
      </div>

      {/* Signed Commits */}
      <div className="flex items-center justify-between p-4 rounded-lg bg-surface-DEFAULT border border-dark-border">
        <div>
          <span className="text-sm font-medium text-gray-300">
            Signed Commits
          </span>
          <p className="text-xs text-gray-500 mt-0.5">
            Require GPG-signed commits for verification
          </p>
        </div>
        <button
          onClick={() =>
            updateGitWorkflow({ signedCommits: !gitWorkflow.signedCommits })
          }
          className={clsx(
            'relative w-11 h-6 rounded-full transition-colors duration-200',
            gitWorkflow.signedCommits ? 'bg-accent' : 'bg-surface-300'
          )}
        >
          <div
            className={clsx(
              'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200',
              gitWorkflow.signedCommits && 'translate-x-5'
            )}
          />
        </button>
      </div>

      {/* PR Requirements */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Pull Request Requirements
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {prRequirementOptions.map((option) => (
            <label
              key={option}
              className={clsx(
                'flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all duration-200 text-sm',
                gitWorkflow.prRequirements.includes(option)
                  ? 'bg-accent/10 border-accent/30 text-accent-300'
                  : 'bg-surface-DEFAULT border-dark-border text-gray-400 hover:border-surface-300'
              )}
            >
              <input
                type="checkbox"
                checked={gitWorkflow.prRequirements.includes(option)}
                onChange={() => togglePRRequirement(option)}
                className="sr-only"
              />
              <div
                className={clsx(
                  'w-4 h-4 rounded border flex items-center justify-center shrink-0',
                  gitWorkflow.prRequirements.includes(option)
                    ? 'bg-accent border-accent'
                    : 'border-surface-300 bg-surface-DEFAULT'
                )}
              >
                {gitWorkflow.prRequirements.includes(option) && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              {option}
            </label>
          ))}
        </div>
      </div>

      {/* Protected Branches */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Protected Branches
        </label>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={branchInput}
            onChange={(e) => setBranchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addProtectedBranch()}
            placeholder="Add a branch name..."
            className="input-field flex-1"
          />
          <button onClick={addProtectedBranch} className="btn-secondary px-4">
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {gitWorkflow.protectedBranches.map((branch) => (
            <span
              key={branch}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-100 border border-dark-border text-sm text-gray-300"
            >
              {branch}
              <button
                onClick={() => removeProtectedBranch(branch)}
                className="text-gray-500 hover:text-red-400 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
